import { browserHistory } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { Map } from 'immutable';

import React, { useCallback, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import Page from 'components/Pages/container';

import MessageBlock from 'components/MessageBlock';

import DineInLocation from 'components/DineInLocation';

import TabOrderSummary from 'components/DineIn/TabOrderSummary';

import { selectServiceId } from 'selectors/browse';
import { selectCurrentMenuTypes, selectDineInPasscode, selectEPOSIntegrated, selectRequirePasscodeForTabOrder } from 'selectors/root';
import { selectSession, selectTabOrder, selectTabPosSyncState, SessionState } from 'selectors/session';
import { fetchTab, setTabMembershipCode } from 'actions/tab';
import { fetchSession } from 'actions/session';
import dineInMessages from 'components/dineInMessages';
import messages from 'components/DineInConfirmation/messages';
import { CartIcon } from 'components/Icons';
import useInterval from 'hooks/useInterval';
import styled from 'styled-components';
import TabLoading from '../../Tabs/TabLoading';
import { StyledButton } from './styles';
import { openLoyaltyModal } from '../../../actions/loyalty';
import { ErrorMessage } from '../../../assets/styles/sharedStyles';

interface Props {
  fetchSession: () => Promise<void>;
  fetchTab: () => Promise<void>;
  openLoyaltyModal: () => void;
  setTabMembershipCode: () => Promise<void>;
}

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
const TAB_POS_SYNC_STATE_ERROR = 'error';
const TAB_POS_SYNC_STATE_SYNCED = 'synced';

const PayErrorMessage = styled(ErrorMessage)`
  text-align: center;
`;

const TabOrder: React.FC<Props> = ({ fetchSession, fetchTab, openLoyaltyModal, setTabMembershipCode }) => {
  const [loading, setLoading] = useState(true);
  const [firstRender, setFirstRender] = useState(true);

  const serviceId = useSelector(selectServiceId);
  const isEPOSIntegrated = useSelector(selectEPOSIntegrated);
  const order = useSelector(selectTabOrder);
  const session = useSelector<Map<string, any>, SessionState>(state => selectSession(state, { serviceId }));
  const passcode = useSelector(selectDineInPasscode);
  const requirePasscodeForTabOrder = useSelector(selectRequirePasscodeForTabOrder);
  const menuTypes = useSelector(selectCurrentMenuTypes);
  const posSyncState = useSelector(selectTabPosSyncState);

  const returnToMenu = useCallback(() => browserHistory.replace(`/service/${serviceId}`), [serviceId]);
  const noOrder = useCallback(
    () => browserHistory.replace(`/service/${serviceId}/order/no-order`),
    [serviceId]
  );

  const canPay: boolean =
    isEPOSIntegrated === false || (isEPOSIntegrated === true && posSyncState === TAB_POS_SYNC_STATE_SYNCED);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const isLoyaltyLogin = queryParams.has('loyalty-login');

    if (isLoyaltyLogin) {
      openLoyaltyModal();
    }
  }, [openLoyaltyModal]);

  // initial fetch
  useEffect(() => {
    if (firstRender) {
      try {
        !session.size ? fetchSession().then(fetchTab) : fetchTab();
        setFirstRender(false);
      } catch (error) {
        // if all else fails, return home. We should probably do something else here, like redirect to no tab order, but this keeps the storyshots happy for now
        browserHistory.push('/');
      }
    }
  }, [fetchSession, fetchTab, firstRender, session.size]);

  // listen for changes on the order
  useEffect(() => {
    // redirect to thankyou on tab closed
    const state = order.get('state');
    if (state && state === 'closed') browserHistory.replace('/thankyou/tab');

    const items = order.get('items');
    if (items) {
      if (!items.size) {
        // if menus exist, return to menu, otherwise noOrder page
        menuTypes.size ? returnToMenu() : noOrder();
      } else setLoading(false);
    }
  }, [noOrder, order, returnToMenu, serviceId, menuTypes.size]);

  const handlePayClick = async () => {
    try {
      await setTabMembershipCode();
    } catch {
      // Do nothing, proceed to payment
    }

    window.location.href = `/checkout/tab/${session.get('id')}`;
  };

  // poll tab once loaded
  useInterval(fetchTab, 5000, !loading);

  return (
    <Page
      settingsPage={true}
      titleMessage={!loading ? dineInMessages.currentTab : null}
      hideBackButton={true}
      Icon={!loading ? CartIcon : null}
    >
      {loading && <TabLoading />}
      {!loading && (
        <>
          <DineInLocation />
          <TabOrderSummary order={order} />
          {menuTypes.size > 0 && (
            <StyledButton
              to={`/service/${serviceId}`}
              label={<FormattedMessage {...dineInMessages.addItems} />}
              primaryButton={true}
            />
          )}
          {canPay ? (
            <StyledButton
              onClick={handlePayClick}
              forceReload={true}
              label={<FormattedMessage {...dineInMessages.payBill} />}
            />
          ) : (
            <PayErrorMessage>
              <FormattedMessage defaultMessage="Sorry, we are currently unable to get your bill, please complete your payment with a member of the team." />
            </PayErrorMessage>
          )}
          {requirePasscodeForTabOrder && !passcode && menuTypes.size > 0 && (
            <MessageBlock body={<FormattedMessage {...messages.passcodeMsg} />} type="info" />
          )}
        </>
      )}
    </Page>
  );
};

export default connect(null, {
  fetchSession,
  fetchTab,
  openLoyaltyModal,
  setTabMembershipCode,
})(TabOrder);
